import type {ExportFormat} from '@github-ui/copilot-conversation-export/types'
import {ArchiveIcon, DownloadIcon, PackageIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'

export interface ConversationActionsMenuProps {
  onRename?: () => void | Promise<void>
  /** When provided, Export renders a JSON / Markdown submenu that reports the chosen format. */
  onExport?: (format: ExportFormat) => void | Promise<void>
  onArchive?: () => void | Promise<void>
  onUnarchive?: () => void | Promise<void>
  onDelete?: () => void | Promise<void>
  /** Portal container name forwarded to the Export submenu overlay. */
  portalContainerName?: string
  showArchive?: boolean
}

export function ConversationExportMenuItem({
  onExport,
  portalContainerName,
}: Pick<ConversationActionsMenuProps, 'onExport' | 'portalContainerName'>) {
  return onExport ? (
    <ActionMenu>
      <ActionMenu.Anchor>
        <ActionList.Item>
          <ActionList.LeadingVisual>
            <DownloadIcon />
          </ActionList.LeadingVisual>
          Export
        </ActionList.Item>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay portalContainerName={portalContainerName}>
        <ActionList>
          <ActionList.Item aria-label="Export conversation as JSON" onSelect={() => void onExport('json')}>
            JSON
          </ActionList.Item>
          <ActionList.Item aria-label="Export conversation as Markdown" onSelect={() => void onExport('markdown')}>
            Markdown
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  ) : (
    <ActionList.Item disabled>
      <ActionList.LeadingVisual>
        <DownloadIcon />
      </ActionList.LeadingVisual>
      Export
    </ActionList.Item>
  )
}

/**
 * Data-free conversation actions. Actions are disabled when their handler is absent so an unavailable action is not a misleading enabled no-op.
 */
export function ConversationActionsMenu({
  onRename,
  onExport,
  onArchive,
  onUnarchive,
  onDelete,
  portalContainerName,
  showArchive = false,
}: ConversationActionsMenuProps) {
  return (
    <>
      <ActionList.Item disabled={!onRename} onSelect={onRename ? () => void onRename() : undefined}>
        <ActionList.LeadingVisual>
          <PencilIcon />
        </ActionList.LeadingVisual>
        Rename
      </ActionList.Item>
      <ConversationExportMenuItem onExport={onExport} portalContainerName={portalContainerName} />
      {showArchive && (
        <ActionList.Item
          disabled={!onArchive && !onUnarchive}
          onSelect={onUnarchive ? () => void onUnarchive() : onArchive ? () => void onArchive() : undefined}
        >
          <ActionList.LeadingVisual>{onUnarchive ? <PackageIcon /> : <ArchiveIcon />}</ActionList.LeadingVisual>
          <span>{onUnarchive ? 'Unarchive session' : 'Archive session'}</span>
        </ActionList.Item>
      )}
      <ActionList.Item variant="danger" disabled={!onDelete} onSelect={onDelete ? () => void onDelete() : undefined}>
        <ActionList.LeadingVisual>
          <TrashIcon />
        </ActionList.LeadingVisual>
        Delete
      </ActionList.Item>
    </>
  )
}
